/**
 * svdy-platform-web — business API client (Wave 3.0).
 *
 * Talks to the svdy-platform unified backend (attendance + processes +
 * organization + subscription + operator endpoints — one deployment,
 * one axios instance).
 *
 * Responsibilities:
 *   1. Attach the access_token as Bearer on every request (per ADR-008:
 *      access_token is the API credential, not id_token).
 *   2. Attach `X-Organization-Id` so the backend's org-context middleware
 *      can scope the request to the caller's active tenant.
 *   3. Handle 401 responses:
 *      a. If the body carries `detail.error === "step_up_required"` (ADR-014
 *         D6), await the StepUpModal via `requestStepUp()`, then retry with
 *         the fresh access_token.
 *      b. Otherwise try a token refresh (deduplicated against concurrent
 *         401s — multi-tab races here trigger server-side reuse detection
 *         which kicks the user out, see ADR-008 §P0-4 + ADR-014 D3).
 *      c. If refresh fails, clear tokens and redirect to sign-in.
 *   4. Unify non-2xx responses into `ApiError` so callers can branch on
 *      `err.status` without parsing axios's error shape.
 *
 * Design note: this is the axios counterpart of
 * svdy-website/src/api/platformApi.ts. The refresh dedup pattern and the
 * step-up branching are verbatim — the backend-facing contract is identical.
 */

import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

import {
  clearTokens,
  getAccessToken,
  refreshSession,
  saveCurrentReturnTo,
} from '../auth/authService';
import { isStepUpCancelled, requestStepUp } from './stepup';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/**
 * svdy-platform's live URL shape (per app/main.py include_router prefix):
 *
 *     https://api.svdy.com/v1/organization/profile
 *     http://127.0.0.1:8001/v1/organization/profile   (local dev)
 *
 * The service is mounted at bare `/v1` — there is NO `/api/` prefix on
 * svdy-platform (unlike svdy-authorization, which mounts at `/api/v1`).
 * Every service file under `src/api/` encodes the `/v1/...` path
 * segment explicitly, so the baseURL here must be the origin only.
 *
 * Accidentally setting this to a value that already ends in `/api/v1`
 * (e.g. the legacy mock-era `REACT_APP_API_URL`) double-prefixes and
 * 404s. We deliberately do NOT fall back to `REACT_APP_API_URL` here
 * to prevent that class of config drift.
 */
const PLATFORM_API_URL =
  process.env.REACT_APP_PLATFORM_API_URL || 'http://127.0.0.1:8001';

const ORG_ID_STORAGE_KEY = 'current_organization_id';
const ROLE_CODE_STORAGE_KEY = 'current_role_code';

// ---------------------------------------------------------------------------
// Organization context
// ---------------------------------------------------------------------------

export function getCurrentOrgId(): string | null {
  return localStorage.getItem(ORG_ID_STORAGE_KEY);
}

export function setCurrentOrgId(orgId: string, roleCode?: string | null): void {
  localStorage.setItem(ORG_ID_STORAGE_KEY, orgId);
  if (roleCode) {
    localStorage.setItem(ROLE_CODE_STORAGE_KEY, roleCode);
  } else {
    localStorage.removeItem(ROLE_CODE_STORAGE_KEY);
  }
}

export function clearCurrentOrgId(): void {
  localStorage.removeItem(ORG_ID_STORAGE_KEY);
  localStorage.removeItem(ROLE_CODE_STORAGE_KEY);
}

export function getCurrentRoleCode(): string | null {
  return localStorage.getItem(ROLE_CODE_STORAGE_KEY);
}

// ---------------------------------------------------------------------------
// Unified error type
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;
  readonly url?: string;

  constructor(status: number, detail: unknown, url?: string) {
    // Callers expect `err.message` to be human-readable; fall back to
    // the HTTP status text when the backend's `detail` field isn't a
    // plain string.
    //
    // New backend convention (2026-05-19): structured errors return
    // `{detail: {code, message, ...}}` for machine-readable handling
    // (e.g. license_required → frontend can render Upgrade button
    // instead of toast). Legacy errors are plain strings.
    const detailObj = (detail as { detail?: unknown } | null)?.detail ?? detail;
    const message =
      typeof detail === 'string'
        ? detail
        : typeof detailObj === 'string'
          ? detailObj
          : (detailObj as { message?: string; error?: string } | null)?.message ||
            (detailObj as { message?: string; error?: string } | null)?.error ||
            (detail as { detail?: string; error?: string } | null)?.error ||
            `Request failed with status ${status}`;
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.url = url;
  }
}

// ---------------------------------------------------------------------------
// Request / response options
// ---------------------------------------------------------------------------

export interface PlatformRequestConfig extends AxiosRequestConfig {
  /** Omit the X-Organization-Id header (for endpoints that operate cross-org
   * or before org context is resolved, e.g. GET /me/organizations). */
  skipOrgHeader?: boolean;
}

// Axios doesn't type-expose custom config fields without a module-augment;
// keep our flags on a typed superset that callers import.
type EnrichedConfig = InternalAxiosRequestConfig &
  PlatformRequestConfig & { _retry?: boolean };

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

const platformApi: AxiosInstance = axios.create({
  baseURL: PLATFORM_API_URL,
  timeout: 15000,
});

platformApi.interceptors.request.use((config) => {
  const cfg = config as EnrichedConfig;

  // Guarantee an AxiosHeaders instance so .set() is always available.
  if (!(cfg.headers instanceof AxiosHeaders)) {
    cfg.headers = new AxiosHeaders(cfg.headers);
  }

  const token = getAccessToken();
  if (token) {
    cfg.headers.set('Authorization', `Bearer ${token}`);
  }

  if (!cfg.skipOrgHeader) {
    const orgId = getCurrentOrgId();
    if (orgId) {
      cfg.headers.set('X-Organization-Id', orgId);
    }
  }
  return cfg;
});

// ---------------------------------------------------------------------------
// Response interceptor: step-up → refresh → sign-in
// ---------------------------------------------------------------------------

platformApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as EnrichedConfig | undefined;

    // 403 "Not a member of this organization" — the stored
    // `current_organization_id` is stale (org was switched, user removed
    // from that tenant, etc.). Self-heal: clear the id and redirect the
    // user to the OrgGate so they can pick / create a valid one.
    // Only triggers on 403 from business endpoints, never on the
    // `/me/organizations` / `POST /organizations` calls (which send
    // skipOrgHeader=true and so wouldn't hit this membership check).
    if (error.response?.status === 403) {
      const detail = (error.response.data as { detail?: string })?.detail;
      if (typeof detail === 'string' && /member of this organization/i.test(detail)) {
        saveCurrentReturnTo();
        clearCurrentOrgId();
        if (
          typeof window !== 'undefined' &&
          !window.location.pathname.startsWith('/auth/')
        ) {
          window.location.replace('/auth/select-org');
        }
        return Promise.reject(toApiError(error));
      }
    }

    if (!original || error.response?.status !== 401 || original._retry) {
      return Promise.reject(toApiError(error));
    }

    // Step-up auth (ADR-014 D6): the backend asks for a recent credential
    // confirmation. Modal handles password → /reauthenticate, persists the
    // new access_token to localStorage, then we retry.
    const detail = (error.response.data as { detail?: { error?: string } })
      ?.detail;
    if (detail?.error === 'step_up_required') {
      original._retry = true;
      try {
        const newToken = await requestStepUp();
        const headers = AxiosHeaders.from(original.headers);
        headers.set('Authorization', `Bearer ${newToken}`);
        original.headers = headers;
        return platformApi(original);
      } catch (stepUpError) {
        // A DECLINED prompt is not a failure: pass the cancellation through so the
        // caller can stop silently instead of rendering the step-up demand back at
        // someone who just refused it. Anything else (reauthenticate failed, no
        // modal mounted) still surfaces as the original 401.
        if (isStepUpCancelled(stepUpError)) return Promise.reject(stepUpError);
        return Promise.reject(toApiError(error));
      }
    }

    // Silent refresh (deduplicated against multi-tab races).
    original._retry = true;
    const refreshResult = await refreshSession();
    if (refreshResult === 'refreshed') {
      const token = getAccessToken();
      const headers = AxiosHeaders.from(original.headers);
      if (token) headers.set('Authorization', `Bearer ${token}`);
      original.headers = headers;
      return platformApi(original);
    }

    if (refreshResult === 'unavailable') {
      return Promise.reject(toApiError(error));
    }

    saveCurrentReturnTo();
    clearTokens();
    clearCurrentOrgId();
    if (
      typeof window !== 'undefined' &&
      !window.location.pathname.startsWith('/auth/')
    ) {
      window.location.replace('/onboarding');
    }
    return Promise.reject(toApiError(error));
  },
);

function toApiError(error: AxiosError): ApiError {
  const status = error.response?.status ?? 0;
  const data = error.response?.data;
  const url = error.config?.url;
  // Pass the raw body through so callers can introspect it (e.g. to show
  // field-level validation messages from FastAPI's detail array).
  return new ApiError(status, data ?? error.message, url);
}

// ---------------------------------------------------------------------------
// Convenience helpers (thin wrappers; return payloads, not AxiosResponse)
// ---------------------------------------------------------------------------

export const http = {
  get: <T = unknown>(path: string, config: PlatformRequestConfig = {}) =>
    platformApi.get<T>(path, config).then((r) => r.data),
  post: <T = unknown>(
    path: string,
    body?: unknown,
    config: PlatformRequestConfig = {},
  ) => platformApi.post<T>(path, body, config).then((r) => r.data),
  patch: <T = unknown>(
    path: string,
    body?: unknown,
    config: PlatformRequestConfig = {},
  ) => platformApi.patch<T>(path, body, config).then((r) => r.data),
  put: <T = unknown>(
    path: string,
    body?: unknown,
    config: PlatformRequestConfig = {},
  ) => platformApi.put<T>(path, body, config).then((r) => r.data),
  delete: <T = unknown>(path: string, config: PlatformRequestConfig = {}) =>
    platformApi.delete<T>(path, config).then((r) => r.data),
};

export default platformApi;
